import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Loader2, Mail, Lock, User, Phone, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { useStudioAuth } from "@/context/StudioAuthContext";
import { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/data";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function beginGoogleSignIn() {
  const redirectUrl = window.location.origin + "/studio/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function StudioLogin() {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", city: "" });
  const [loading, setLoading] = useState(false);
  const { customer, ready, login, register } = useStudioAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (ready && customer) nav("/studio/dashboard", { replace: true });
  }, [ready, customer, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email.trim().toLowerCase(), form.password);
        toast.success("Welcome back!");
      } else {
        if (!form.name || !form.email || !form.password) {
          toast.error("Please fill in name, email and password.");
          return;
        }
        if (form.password.length < 6) {
          toast.error("Password must be at least 6 characters.");
          return;
        }
        await register({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          phone: form.phone || null,
          city: form.city || null,
        });
        toast.success("Account created!");
      }
      nav("/studio/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full border border-[#143D63]/20 bg-[#F9F8F6] pl-11 pr-4 py-3 text-sm text-[#143D63] focus:border-[#F8C21C] focus:outline-none";

  return (
    <main data-testid="studio-login-page" className="min-h-screen relative overflow-hidden bg-[#143D63] flex items-center justify-center px-5 sm:px-8 py-16">
      <Seo
        title={`ZEBO Studio ${mode === "login" ? "Sign in" : "Sign up"} — AI Interior Design Portal`}
        description="Sign in to ZEBO Studio and generate AI interior design ideas from your own floor plans, wall photos and room photos."
        path="/studio"
      />
      <div className="absolute inset-0 opacity-25 pointer-events-none bg-[radial-gradient(circle_at_20%_10%,#F8C21C_0%,transparent_35%),radial-gradient(circle_at_80%_90%,#8B0000_0%,transparent_35%)]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md bg-[#F9F8F6] border border-[#F8C21C]/30 shadow-2xl"
      >
        <div className="p-8 sm:p-10">
          <img src={BRAND.logo} alt={BRAND.full} className="h-12 w-auto object-contain mb-6" />
          <p className="overline text-[#8B0000] mb-3 flex items-center gap-2">
            <Sparkles size={13} /> ZEBO Studio
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#143D63] font-light leading-tight">
            {mode === "login" ? "Welcome back." : "Create your Studio account."}
          </h1>
          <p className="text-[#143D63]/70 text-sm mt-3">
            {mode === "login"
              ? "Sign in to keep generating and saving your AI room designs."
              : "Free unlimited AI generation — upload a floor plan, wall or room and see it redesigned in seconds."}
          </p>

          <div className="mt-7 flex gap-1 bg-white border border-[#143D63]/15 p-1 w-fit">
            <button
              data-testid="studio-tab-login"
              type="button"
              onClick={() => setMode("login")}
              className={`px-5 py-2 text-sm font-bold transition-colors ${mode === "login" ? "bg-[#143D63] text-[#F9F8F6]" : "text-[#143D63]"}`}
            >
              Sign in
            </button>
            <button
              data-testid="studio-tab-signup"
              type="button"
              onClick={() => setMode("signup")}
              className={`px-5 py-2 text-sm font-bold transition-colors ${mode === "signup" ? "bg-[#143D63] text-[#F9F8F6]" : "text-[#143D63]"}`}
            >
              Create account
            </button>
          </div>

          <button
            type="button"
            data-testid="studio-google-btn"
            onClick={beginGoogleSignIn}
            className="mt-6 w-full flex items-center justify-center gap-3 border border-[#143D63]/25 bg-white text-[#143D63] px-5 py-3 text-sm font-semibold hover:border-[#143D63] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8 3l5.7-5.7C34.3 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-4.4 6.1-7.5 11.1-7.5 3.1 0 5.9 1.1 8 3l5.7-5.7C34.3 6.1 29.5 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.4 0 10.2-2 13.9-5.2l-6.4-5.4c-2 1.4-4.6 2.6-7.5 2.6-5.2 0-9.6-3.4-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.4 5.4c-.4.4 6.5-4.8 6.5-15.1 0-1.3-.1-2.3-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-[#143D63]/15" />
            <span className="text-xs text-[#143D63]/40 uppercase tracking-wider">or</span>
            <span className="flex-1 h-px bg-[#143D63]/15" />
          </div>

          <form data-testid="studio-auth-form" onSubmit={submit} className="mt-2 space-y-4">
            {mode === "signup" && (
              <div className="relative">
                <User size={16} className="absolute top-3.5 left-4 text-[#143D63]/50" />
                <input
                  data-testid="studio-name"
                  className={field}
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            )}
            <div className="relative">
              <Mail size={16} className="absolute top-3.5 left-4 text-[#143D63]/50" />
              <input
                data-testid="studio-email"
                type="email"
                className={field}
                placeholder="you@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute top-3.5 left-4 text-[#143D63]/50" />
              <input
                data-testid="studio-password"
                type="password"
                className={field}
                placeholder="Password (min 6 characters)"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>
            {mode === "signup" && (
              <>
                <div className="relative">
                  <Phone size={16} className="absolute top-3.5 left-4 text-[#143D63]/50" />
                  <input
                    data-testid="studio-phone"
                    className={field}
                    placeholder="Phone (optional)"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="relative">
                  <MapPin size={16} className="absolute top-3.5 left-4 text-[#143D63]/50" />
                  <input
                    data-testid="studio-city"
                    className={field}
                    placeholder="City (optional)"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </div>
              </>
            )}
            <button
              data-testid="studio-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#143D63] text-[#F9F8F6] px-7 py-3.5 font-bold text-sm hover:bg-[#F8C21C] hover:text-[#143D63] transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="text-xs text-[#143D63]/60 mt-6 leading-relaxed">
            By continuing you agree to ZEBO&apos;s <a href="/terms" className="link-underline text-[#8B0000]">Terms</a> and <a href="/privacy" className="link-underline text-[#8B0000]">Privacy Policy</a>.
          </p>
        </div>
      </motion.div>

      {/* SEO-rich content below the fold — informative for crawlers and returning users */}
      <div className="relative z-10 max-w-3xl mx-auto mt-12 text-[#F9F8F6]/90 px-2">
        <div className="border-t border-[#F8C21C]/30 pt-10 space-y-6">
          <div>
            <h2 className="font-serif text-2xl text-[#F8C21C] mb-3">What is ZEBO Studio?</h2>
            <p className="text-sm leading-relaxed">
              ZEBO Studio is a free AI-powered interior design portal built by ZEBO India Private Limited — Kolkata&apos;s premier interior design firm.
              Upload a photograph of your existing room, a bare wall you want to transform, or an architectural floor plan, and our advanced generative AI
              produces a photorealistic redesign in seconds. You can iterate through unlimited styles — Modern Minimal, Warm Contemporary, Luxe Classic,
              Scandinavian, Indian Contemporary, Industrial — and preview each idea as both a flat 2D render and a fully immersive 360° walkthrough.
              Every generated design is saved to your private gallery and can be booked into a real consultation with our senior designers in one click.
            </p>
          </div>
          <div>
            <h2 className="font-serif text-2xl text-[#F8C21C] mb-3">Why homeowners across Kolkata love it</h2>
            <ul className="text-sm leading-relaxed list-disc pl-5 space-y-1.5">
              <li>See your dream living room, modular kitchen, wardrobe or bedroom before spending a rupee on materials.</li>
              <li>Test bold ideas — brass panelling, sage-green cabinets, terrazzo flooring — risk-free.</li>
              <li>Share concepts instantly with your family on WhatsApp; every render is a downloadable image.</li>
              <li>Book a free site visit whenever you love a design; ZEBO&apos;s team then builds it to spec.</li>
              <li>Works equally well for commercial spaces — cafés, offices, retail shops, salons, restaurants.</li>
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl text-[#F8C21C] mb-3">Free to use. Yours forever.</h2>
            <p className="text-sm leading-relaxed">
              Signing up takes ten seconds — email &amp; password, or one-tap Google Sign-in. There are no trial limits, no credit-card gates, and no hidden fees.
              Whether you&apos;re planning a full 3BHK renovation in Ballygunge, a modular kitchen refresh in Salt Lake, a wardrobe overhaul in Rajdanga, or a
              café fit-out in New Town, ZEBO Studio helps you go from vague idea to visual clarity long before a single tile is laid.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
